import { OnlineBar } from "../version/Collab";
import { useEffect, useRef, useState } from "react";
import { fetchAgents, type Agent } from "../../lib/api";
import { currentToken } from "../../lib/session";
import { useT, type UiLang } from "../../lib/i18n";
import { LangSwitch } from "./LangSwitch";
import { formatAt } from "../../lib/time";
import { useTz } from "../../lib/tz-context";
import { ApiLensToggle } from "../apilens/ApiLens";
import { McpDrawer } from "./McpDrawer";

export type { Agent } from "../../lib/api";

/** 点击外部关闭。顶栏两个下拉都要，抽出来避免各写一份 listener */
function useClickAway<T extends HTMLElement>(onAway: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onAway();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onAway]);
  return ref;
}

function AgentSelect({ value, initialAgents = [], onChange }: {
  value: string | null;
  initialAgents?: Agent[];
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  /**
   * `agents` 这批结果是哪个关键词搜出来的。
   *
   * 只为回车服务：防抖期间 `q` 已经变了而 `agents` 还是上一次的，
   * 光看 `state === "idle"` 判断不出来（`setState("loading")` 在 setTimeout 里，
   * 那 300ms 内 state 仍是 idle）。见 onKeyDown 处的说明。
   */
  const [listQ, setListQ] = useState("");
  const hasToken = !!currentToken();
  const t = useT();
  const ref = useClickAway<HTMLDivElement>(() => setOpen(false));

  /**
   * 关键词发给服务端搜，不在前端过滤。
   *
   * 中台一次只回前 N 条，代理总数远不止；只拉一次再本地 filter 的话，
   * 搜「gavin」会显示「无匹配代理」——但它其实存在，只是不在那 N 条里。
   * 防抖 300ms，避免逐字打字把中台打满。
   */
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const t = setTimeout(() => {
      setState("loading");
      fetchAgents(q.trim())
        .then((a) => { if (alive) { setAgents(a); setListQ(q.trim()); setState("idle"); } })
        .catch(() => { if (alive) setState("error"); });
    }, q ? 300 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q]);

  /*
   * 首屏当前代理由 App 已完成的 /agents 结果解析；TopBar 不能再按 value 补拉一次，
   * 否则首帧 agentOptions 还没传下来时两个 effect 会并发打同一个接口。
   * resolved 只保存用户从搜索结果主动点选的代理，让下拉关闭后立即显示名称。
   */
  const [resolved, setResolved] = useState<Agent | null>(null);
  const cur = agents.find((a) => a.a0 === value)
    ?? initialAgents.find((a) => a.a0 === value)
    ?? (resolved?.a0 === value ? resolved : undefined);
  useEffect(() => {
    // 下拉没打开时用 App 已拉到的列表起步；搜索结果为空时不能回退这份列表，
    // 否则输入一个不存在的关键词仍会显示启动时的代理。
    if (!open && initialAgents.length) setAgents(initialAgents);
  }, [initialAgents, open]);
  const list = agents;
  const degraded = agents.some((a) => a.degraded);
  const pick = (a: Agent) => { setResolved(a); onChange(a.a0); setOpen(false); };
  return (
    <div ref={ref} className="relative flex-none">
      <div className="relative inline-flex items-center">
        <input
          value={open ? q : cur ? `${cur.a0} - ${cur.name}` : (value ?? "")}
          placeholder={t("top.agentPlaceholder")}
          onFocus={() => { setOpen(true); setQ(""); }}
          /*
           * 已经有焦点时（刚选完 / 按过 Esc，光标还在框里）再点不会触发 onFocus，接着打字就拼在「12 - 代理 12」后面、
           * 一个都匹配不到（10-10 从零建代理实测）。点一下也清空开搜；没打开时打的字只取新敲的那部分
           */
          onClick={() => { if (!open) { setOpen(true); setQ(""); } }}
          onChange={(e) => {
            const shown = cur ? `${cur.a0} - ${cur.name}` : (value ?? "");
            const v = e.target.value;
            setOpen(true);
            setQ(!open && shown && v.startsWith(shown) ? v.slice(shown.length) : v);
          }}
          /*
           * 键盘也要能选。此前这里只有 onFocus/onChange，选中只走选项行的 onClick——
           * 搜完必须伸手去点，敲回车毫无反应且没有任何提示。
           * 仓库里 SearchSelect 的搜索框早就是这么做的，这里只是没跟上。
           */
          onKeyDown={(e) => {
            /*
             * 输入法组词期间的按键属于输入法，不属于这里。
             *
             * 中文输入法下打「ludo」再敲回车让它上屏，这一下同样会走到 onKeyDown。
             * 下面那层过期保护挡不住它——组词稍一停顿，防抖跑完、结果也回来了，
             * `listQ === q.trim()` 恰好成立，于是「确认输入」被当成「选中代理」，
             * 当场把人切到第一条候选的租户上。
             * keyCode 229 是 isComposing 之前的老信号，一并认，成本几乎为零。
             */
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "Escape") {
              setOpen(false);
              // 必须一起失焦：input 还聚焦着的话，再点它不会触发 onFocus，下拉就再也打不开了
              e.currentTarget.blur();
              return;
            }
            if (e.key !== "Enter") return;
            e.preventDefault();
            /*
             * **只在候选确实是当前关键词搜出来的时候才认回车。**
             *
             * 候选来自服务端（防抖 300ms），打完字立刻回车时 `list` 还是上一个
             * 关键词的结果。切代理会换掉整个后台的数据视图，选错就是在别人的租户上
             * 改配置——宁可这一下不响应，也不能拿过期列表凑一个出来。
             * （SearchSelect 是本地过滤、候选与输入同步，所以那边不需要这层判断。）
             */
            if (state === "idle" && listQ === q.trim() && list.length) {
              pick(list[0]);
              /*
               * 选完必须失焦，理由同上面的 Escape：焦点留在 input 上时，
               * 用户想再换一个代理去点它**不会触发 onFocus**，下拉再也打不开。
               * 鼠标点选那条路径不用管——点选项行本身就把焦点从 input 移走了。
               * （实测踩到：回车选完 12，再点输入框想换 13，点了没反应。）
               */
              e.currentTarget.blur();
            }
          }}
          className={`h-control w-[190px] rounded-base border bg-white pl-2 pr-6 text-base text-ink outline-none ${open ? "border-primary" : "border-line"}`}
        />
        {cur && (
          <button type="button" title={t("top.clear")} onClick={() => { onChange(null); setQ(""); }}
            className="absolute right-1.5 text-[13px] leading-none text-ink-placeholder hover:text-ink">⊗</button>
        )}
      </div>
      {open && (
        <div className="absolute right-0 top-9 z-[60] max-h-[280px] min-w-[210px] overflow-y-auto rounded-base border border-line-lighter bg-white p-1 shadow-light">
          {degraded && (
            <div className="mb-1 rounded-sm bg-warning-50 px-2.5 py-1 text-xs text-warning-700">
              {t("agent.degraded")}
            </div>
          )}
          {state === "loading" && <div className="px-2.5 py-1.5 text-sm text-ink-placeholder">{t("agent.searching")}</div>}
          {state === "error" && (
            <div className="px-2.5 py-1.5 text-sm text-danger">
              {t("agent.loadFailed")}{!hasToken && t("agent.needSuperAdmin")}
            </div>
          )}
          {list.map((a) => (
            <div
              key={a.a0}
              // 没有自己配置的代理**照样可选**——它读全局默认那份，能正常用。
              // 之前这里把它们置灰，是基于「没配库就用不了」的旧假设。
              onClick={() => pick(a)}
              title={a.hasOwnConfig ? undefined : t("agent.inheritedTip")}
              className={
                `cursor-pointer rounded-sm px-2.5 py-1.5 text-base ` +
                (a.a0 === value
                  ? "bg-primary-50 text-primary"
                  : "text-ink-regular hover:bg-fill-light")
              }
            >
              {a.a0} - {a.name}
              {!a.hasOwnConfig && (
                <span className="ml-1.5 text-xs text-ink-placeholder">{t("agent.inherited")}</span>
              )}
            </div>
          ))}
          {state === "idle" && !list.length && (
            <div className="px-2.5 py-1.5 text-sm text-ink-placeholder">{q ? t("agent.noMatch", { q }) : t("agent.empty")}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function TopBar({
  agent, agents, onAgent, uiLang, onUiLang, user, onOpenLog, onLogout, env, compactOnNarrow = false,
}: {
  agents?: Agent[];
  agent: string | null; onAgent: (id: string | null) => void;
  uiLang: UiLang; onUiLang: (v: UiLang) => void; user: string;
  onOpenLog: () => void;
  onLogout: () => void;
  /** 后端 /admin/api/health 报的环境。写的是哪个库，看这个 */
  env?: string;
  /** 学习页在窄屏只保留身份与语言，配置页仍展示完整工具。 */
  compactOnNarrow?: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const [mcp, setMcp] = useState(false);
  const ref = useClickAway<HTMLDivElement>(() => setMenu(false));
  const t = useT();
  const tz = useTz();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <header className={`flex h-12 items-center gap-2.5 border-b border-line-light bg-white ${compactOnNarrow ? "px-3 md:px-page" : "px-page"}`}>
      <span className={`${compactOnNarrow ? "min-w-0 truncate " : ""}text-base font-semibold text-ink`}>{t("app.title")}</span>
      {/*
        当前写的是哪个库。`npm run admin:dev`（本地 MySQL）与 `admin:test`
        （共享 test 库）打开的界面一模一样，只差地址栏端口号——而干活时眼睛在页面上。
        非 dev 一律标红：test / main 都是别人也在用的库，删之前该犹豫一下。
        文案不进 i18n：环境名本来就是英文标识，翻译反而看不懂。
      */}
      {env && (
        <span className={`flex-none rounded-sm px-1.5 py-0.5 text-xs font-semibold ${
          env === "dev" ? "bg-fill-light text-ink-placeholder" : "bg-danger-50 text-danger-700"
        }`}>
          {env.toUpperCase()}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2.5">
        {/* 当前租户的当地时间。页面里所有时间都按它渲染，放在顶栏是为了让人
            切换代理时立刻看出时区变了，而不是等配错了时间才发现。 */}
        <div className={compactOnNarrow ? "hidden md:contents" : "contents"}>
        <span className="flex-none text-xs text-ink-placeholder" title={t("tz.tipLong", { tz })}>
          {formatAt(now, tz)}
        </span>
        <span className="flex-none text-base font-semibold text-ink">{t("top.agent")}</span>
        <AgentSelect value={agent} initialAgents={agents} onChange={onAgent} />
        <OnlineBar />
        <ApiLensToggle />
        </div>
        {/* 运营后台 MCP：让自己的 Claude 配卡包、批量传图（令牌、接入方法、示例都在抽屉里） */}
        <button
          type="button" onClick={() => setMcp(true)}
          className={`${compactOnNarrow ? "hidden md:inline-flex " : "inline-flex "}flex-none items-center rounded-base border border-primary-100 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-800 hover:bg-primary-100`}
        >
          {t("mcp.entry")}
        </button>
        <LangSwitch value={uiLang} onChange={onUiLang} />
        <div ref={ref} className="relative">
          <div onClick={() => setMenu(!menu)} className="flex cursor-pointer items-center gap-1.5">
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
              {user.slice(0, 1).toUpperCase()}
            </span>
            <span className={`${compactOnNarrow ? "hidden md:inline " : ""}text-base text-ink-regular`}>{user}</span>
            <span className="text-[10px] text-ink-placeholder">▾</span>
          </div>
          {menu && (
            <div className="absolute right-0 top-8 z-[60] min-w-[130px] rounded-base border border-line-lighter bg-white p-1 shadow-light">
              {/*
                没有「修改密码」：配置台不持有任何自己的凭据——登录态整个来自
                super-admin 的 token。放一个改不了密码的入口只会误导人，
                真要改密码得去 super-admin。
              */}
              <div
                onClick={() => { setMenu(false); onOpenLog(); }}
                className="cursor-pointer rounded-sm px-2.5 py-1.5 text-sm text-ink-regular hover:bg-fill-light"
              >
                {t("top.operationLog")}
              </div>
              <div
                onClick={() => { setMenu(false); onLogout(); }}
                className="cursor-pointer rounded-sm px-2.5 py-1.5 text-sm text-danger hover:bg-fill-light"
              >
                {t("top.logout")}
              </div>
            </div>
          )}
        </div>
      </div>
      {mcp && <McpDrawer a0={agent} onClose={() => setMcp(false)} />}
    </header>
  );
}
